import { useState, Fragment, useEffect } from 'react';
import { TextField, Autocomplete, Paper, IconButton, Divider, createFilterOptions, FormHelperText } from '@mui/material';
import styled from '@emotion/styled';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import ErrorMessage from '../MuiFields/ErrorMessage';

import { useField, Field, useFormikContext } from 'formik';

import { get, update } from 'lodash';

import Dialog from '../utilities/Dialog';

// context of additional state
import { useFormData } from '../../contexts/FormData';

// new value context
import { NewValueContext, useNewValue } from '../../contexts/NewValue';

// hooks
import useInputDebounce from '../../hooks/useInputDebounce';


const CustomAutocomplete = styled(Autocomplete)`
 
`;

const filter = createFilterOptions();

const updateFields = (option, sfv, formUpdates, name = null) => {
  if (sfv && (formUpdates?.length || name)) {
    let updates = {};
    if (formUpdates?.length) {
      formUpdates.forEach(el => {
        updates[el.name] = option ? get(option, el.field) : '';
      });
    }
    let field = name ? {[name]: option || null} : {}

    sfv(ov => ({ ...ov, ...updates, ...field }));
  }
}

/**
 * AutoCompletSolo - A custom autocomplete component with free solo input
 * 
 * sample usage: const options = useInputDebounce({ url: 'inventory_items', state: true });
 * @optional = displayLabel = (option) => `${option.name} - ${option.title}`
 * <AutoCompleteSolo label="Stylist" fieldName="stylist_id" newItemTitle="Stylist" {...options} />
 * 
 * @required setValue {Function} - State setter for input value
 * @required value {string} - Current input value
 * @required fieldName {string} - Option object property to use for values
 * 
 * @optional handleInputChange {Function} - Callback on input change
 * @optional loading {boolean} - Loading state (default: true)
 * @optional options {Array} - Dropdown options (default: [{name: "hello", title: "hello"}])
 * @optional popup {ReactNode} - Custom popup icon (default: null)
 * @optional newItemTitle {string} - Text for "Create new" option (default: "Item")
 * @optional displayLabel {Function} - Custom label formatter
 * @optional ...props - Additional CustomAutocomplete props
 */

const AutoCompleteSolo = ({fieldName: optionField, popup = null, newItemTitle = "Item", displayLabel, size = "small", placeholder = "Search", useForm = false, FormComponent = null, update, serverOptions, forceServer = false, displayField, setDisplayField, url, style, select, updates, formUpdates, saveOption = false, saveOptionField = null, conditionStr, server, onlyPrefetch = false, simple = false, initialValue, ...props}) => {

  const { formValues, setFormValues } = useFormData() || {};

  const { newValue: newCreatedValue } = useNewValue() || {};

  let renderLabel = displayField || optionField;

  const { handleInputChange, loading, options, value: inputValue, setValue } = useInputDebounce({ url, state: true, select, serverOptions, conditionStr, forceServer, onlyPrefetch, initialValue });


  const [field, meta, helpers] = useField(props);

  const { onBlur, onChange, name, value } = field;

  const [openDialog, setOpenDialog] = useState(false)

  const handleChangeInput = (e, val, reason) => {
    let change = ["input", "clear"].includes(reason) ? true : false;
    if (change) {
      setValue(val);
      if (handleInputChange) {
        handleInputChange(val, e);
      }
    }
  }

  const handleBlur = async (e) => {
    if (!value) {
      if (simple && inputValue) {
        // Use await to ensure the value is set in Formik state
        await helpers.setValue(inputValue);
        // Manually trigger validation after the value is set
        helpers.setTouched(true);
      } else {
        if (simple) setValue('');
      }
    }
    // Only call the original onBlur if we didn't just manually set a value,
    // or call it after the update to ensure Formik sees the new state.
    onBlur(e);
  }

  useEffect(() => {
    if (newCreatedValue) {
      setTimeout(() => {
        helpers.setValue(newCreatedValue?.[optionField]?.toString());
      }, 0);
    }
  }, [newCreatedValue])

  useEffect(() => {
    if (!value) {
      setTimeout(() => {
        setValue('');
        updateFields(null, setFormValues, formUpdates, saveOption ? saveOptionField || name : null);
      }, 0);
    }

    let findOption = value && options?.find(el => el[optionField]?.toString() === value?.toString());

    if (!findOption && !FormComponent) {
      if (simple) {
        return setValue(value);
      } else {
        return; // in case of true solo, with no ability to create new option
      }
    } 
    setTimeout(() => {
      setValue(findOption ? findOption[renderLabel] : '');
      updateFields(findOption, setFormValues, formUpdates, saveOption ? saveOptionField || name : null);
    }, 0);
  }, [value])

  useEffect(() => {
    if (serverOptions?.length) {
      let findOption = value && options?.find(el => el[optionField]?.toString() === value?.toString());
      if (!findOption && !FormComponent) {
        if (simple) {
          return setValue(value);
        } else {
          return; // in case of true solo, with no ability to create new option
        }
       } 
      setTimeout(() => {
        setValue(findOption ? findOption[renderLabel] : '');
        updateFields(findOption, setFormValues, formUpdates, saveOption ? saveOptionField || name : null);
      }, 0);
    }
  }, [serverOptions, options])

  const handleChange = (e, newValue) => {
   // Update the Formik field value with the selected option
    if (newValue) {
      if (typeof newValue === 'string') {
        helpers.setValue(newValue);
      } else {
        // Update Formik field with the complete object
        helpers.setValue(newValue[optionField]?.toString());
      }
    } else {
      // Clear both the field value and input text when selection is cleared
      helpers.setValue(null);
    }
  };

  return (
    <Fragment>
      <CustomAutocomplete
        loading={loading}
        popupIcon={popup}
        options={options}
        size={size}
        onInputChange={handleChangeInput} 
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option
          }
          if (option.isNewOption) {
            return option.inputValue || '';
          }
          return option[renderLabel]?.toString() || ""
        }}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          let label;
          if (option.isNewOption) {
            label = option.title;
          } else {
            if (displayLabel) {
              label = displayLabel(option);
            } else {
              label = option[renderLabel];
            }
          }
          return (
            <li key={key} {...optionProps}>
              {label}
            </li>
          );
        }}
        // isOptionEqualToValue={(option, value) => {
        //   return option.inputValue === value.inputValue || option[optionField] === value[optionField];
        // }}
        onChange={(e, newValue) => {
          if (newValue && newValue.isNewOption) {
            if (useForm || FormComponent) {
              setOpenDialog(true)
              setTimeout(() => {
                handleChange(e, "");
              }, 5);
            } else {
              handleChange(e, newValue.inputValue);
            }
          } else {
            handleChange(e, newValue);
          }
        }}
        inputValue={inputValue}
        defaultValue={''}
        fullWidth
        freeSolo={false}
        clearOnBlur
        selectOnFocus
        onBlur={handleBlur}
        filterOptions={(options, params) => {
          const inputValue = params?.inputValue || '';

          let filtered = server && !serverOptions?.length
            ? [...options]
            : filter(options, { ...params, inputValue });

          if (useForm || FormComponent) {
            filtered.push({
              isNewOption: true,
              inputValue: inputValue,
              title: inputValue !== ''
                ? `Create New ${newItemTitle}: "${inputValue}"`
                : `Create New ${newItemTitle}`,
            });
          }

          return filtered;
        }}
        {...props}
        renderInput={(params) => {
          return (<TextField {...params} name={props.name} placeholder={placeholder} variant="outlined" style={{backgroundColor: "#fff", ...style}}  />)}
        }
      />
      {meta.touched && meta.error ? <ErrorMessage error={true} message={meta.error} /> : null}
    <Dialog size="md" title={`Create New ${newItemTitle}`} forceOpen={openDialog} forceClose={() => setOpenDialog(false)} text="" saveText={`Save New ${newItemTitle}`} useForm={true}>
      {FormComponent}
    </Dialog>
    </Fragment>
  )
};

const AutoCompleteSoloComponent = (props) => {
  const [newValue, setNewValue] = useState(null);

  const addNewValue = (newOption) => {
    setNewValue(newOption);
  };
  return (
    <NewValueContext.Provider value={{ newValue, addNewValue }}>
      <AutoCompleteSolo {...props} />
    </NewValueContext.Provider>
  )
}

export default AutoCompleteSoloComponent;